import type { ComponentPropsWithoutRef } from 'react'
import { useCMSPageField } from '@/cms/fields'
import { useFavouriteProductIdsQuery } from '@/domain/product/hooks'
import { HeaderDependenciesProvider } from '@/scopes/common/components/Header/dependencies'
import { LanguageSwitch } from '@/scopes/common/components/Header/LanguageSwitch'

import TopNavigationBarItem from '@/scopes/common/components/Header/TopNavigationBarItem'
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider'
import { createLiveSearchApiClient } from '@/scopes/search/search/infrastructure/live-search/api/liveSearchApiClient'
import { mqMin, space, SpaceBetween, tok, tokPx, Typography, zIndex } from '@sunwebgroup/ui-components'

import { Icon } from '@sunwebgroup/ui-components/uds'
import * as React from 'react'
import { styled } from 'styled-components'
import { LiveSearch } from '../../../search/search/pages/SearchPage/components/LiveSearch'
import { AccountMenu } from './AccountMenu'
import { DesktopNavigation } from './DesktopNavigation'
import { MenuTrigger } from './Menu/Trigger'
import { MobileNavigation } from './MobileNavigation'
// TODO: Remove this import when the Header component is connected to Sitecore
import mockData, { mockDataExtra } from './mock-data'
import { Navbar } from './Navbar'

// Extend the props to include hideLiveSearch
interface HeaderProps extends ComponentPropsWithoutRef<'header'> {
  hideLiveSearch?: boolean
  hideNavItems?: boolean
}

Header.FavoritesLink = styled(TopNavigationBarItem)`
  ${Icon} {
    margin-right: ${space('primitives_space_4')};
  }
`

export function Header({ hideLiveSearch = false, hideNavItems = false, ...props }: HeaderProps) {
  const { data: favourites } = useFavouriteProductIdsQuery()
  const links = useConfiguration('links')
  const contextItemId = useCMSPageField('contextItemId')
  const pdpId = useCMSPageField('PDP.id')
  const liveSearchContextItemId = contextItemId ?? pdpId
  const showLiveSearch = !hideLiveSearch && liveSearchContextItemId

  const headerMainMenu = useCMSPageField('Header.mainMenu')
  const mainContent = hideNavItems ? undefined : headerMainMenu?.mainItem ?? mockData
  const extraContent = hideNavItems ? undefined : headerMainMenu?.extraItems ?? mockDataExtra
  return (
    <HeaderDependenciesProvider
      dependencies={{
        liveSearchApiClient: createLiveSearchApiClient({ contextItemId: liveSearchContextItemId }),
      }}
    >
      <Header.Root {...props}>
        <Header.Content>
          <DesktopNavigation data={mainContent} extraData={extraContent} />
          <SpaceBetween direction="x" alignItems="center">
            <LanguageSwitch />
            {showLiveSearch && <LiveSearch />}
            {!hideNavItems && (
              <Header.FavoritesLink as="a" href={links.favourites}>
                <Icon glyph="Heart" />
                <Typography>{favourites?.length ?? 0}</Typography>
              </Header.FavoritesLink>
            )}
            <AccountMenu />
            {!hideNavItems && <MobileNavigation data={mainContent} extraData={extraContent} />}
          </SpaceBetween>
        </Header.Content>
      </Header.Root>
    </HeaderDependenciesProvider>
  )
}

Header.Content = styled.div`
  align-items: center;
  display: flex;
  margin: 0 auto;
  max-width: ${tokPx('primitives_area_size_1440')};
`

Header.Root = styled.header`
  background: ${tok('semantics_views_background_color')};
  padding: ${space('primitives_space_8')};
  position: relative;
  z-index: ${zIndex.header};

  @media ${mqMin('primitives_breakpoint_m_min')} {
    padding-left: ${space('primitives_space_32')};
    padding-right: ${space('primitives_space_32')};
  }
  @media ${mqMin('primitives_breakpoint_l_min')} {
    padding-left: ${space('primitives_space_48')};
    padding-right: ${space('primitives_space_48')};
  }


  ${Header.Content} > ${Navbar} {
    margin-right: auto;

    ${Navbar.Item} {
      &:not(:first-of-type) {
        display: none;
        @media (min-width: ${tokPx('primitives_breakpoint_l_min')}) {
          display: block;
        }
      }

      &:nth-of-type(2) {
        margin-left: ${space('primitives_space_8')};
      }

      &:not(:first-child) > *:first-child {
        padding: ${space('primitives_space_8')} ${space('primitives_space_16')};
      }

      & > ${MenuTrigger}[data-state='open'] {
        background: ${tok('primitives_color_neutral_100')};

        ${Icon} {
          transform: rotate(180deg);
        }
      }
    }
  }
`
